import { getI18n } from '@gympass/i18n';
import { type QueryClient, queryOptions } from '@tanstack/react-query';

import { OTP_DISCRIMINATOR } from 'config/storage';
import type { Language } from 'services/i18n/types';
import { rumService } from 'services/rum';

import {
  createChallenge,
  getVerdict,
  OtpRateLimitedError,
  resumeChallenge,
} from './otp';
import type { ChallengeIdentity } from './types';

/**
 * How long the OTP service keeps a validation record: "purged after 7 days",
 * and a lookup after that returns `NOT_FOUND` (otp-auth-service docs,
 * `index.md`, "Retention"). A hash is useless past it, so no OTP query is kept
 * longer. A final Verdict is also trusted for this long.
 */
export const OTP_RECORD_RETENTION_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * The Discriminator: a random id for this browser, created the first time it is needed and shared by every tab. It is the secret that lets the tabs of one browser resume the same pending challenge, and keeps other browsers out of it.
 */
export function getOtpDiscriminator(): string {
  const stored = localStorage.getItem(OTP_DISCRIMINATOR);
  if (stored) {
    return stored;
  }

  const discriminator = crypto.randomUUID();
  localStorage.setItem(OTP_DISCRIMINATOR, discriminator);
  return discriminator;
}

/**
 * What identifies an Email-validation challenge, for the query key and for both requests. The email is trimmed and lowercased, as the BFF stores it: a caller may hold the typed email or the one the BFF answers, and both must find the same challenge. The SPA never builds a `source_id`: that is the BFF's concern.
 */
function otpChallengeIdentity(email: string): ChallengeIdentity {
  return {
    email: email.trim().toLowerCase(),
    discriminator: getOtpDiscriminator(),
  };
}

/**
 * The one query of an Email-validation challenge, for the enter and for every restart: it resumes the pending challenge of the email in this browser, and creates one only when none is pending. While a challenge is pending, running it again returns the same hash and sends no email.
 *
 * A failed Resume throws and creates nothing: a create would replace the challenge that another tab may be showing.
 *
 * The language is sent on create and is not in the key, so a language change reuses the challenge and sends no email.
 *
 * Every option that could create a challenge on its own (and so send an email) is explicit, so no global default can apply. A challenge is created only when Resume finds none pending, on enter or on a restart.
 */
export function otpChallengeQueryOptions({ email }: { email: string }) {
  const identity = otpChallengeIdentity(email);

  return queryOptions({
    queryKey: ['otp-challenge', identity],
    queryFn: async () => {
      const pending = await resumeChallenge(identity).catch(error => {
        rumService.addError(new Error('[otp] Challenge resume failed'), {
          error,
        });
        throw error;
      });

      if (pending) {
        return pending;
      }

      return createChallenge({
        ...identity,
        locale: getI18n().language as Language,
      }).catch(error => {
        if (!(error instanceof OtpRateLimitedError)) {
          rumService.addError(new Error('[otp] Challenge creation failed'), {
            error,
          });
        }
        throw error;
      });
    },
    staleTime: Infinity,
    // The hash is useless once the service purges its record.
    gcTime: OTP_RECORD_RETENTION_MS,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: false,
    retry: false,
    // Do not create again when a page mounts on a challenge that failed.
    retryOnMount: false,
  });
}

/**
 * The Verdict of a challenge, keyed by the hash alone: the only thing the
 * request sends.
 *
 * A `pending` Verdict is always read again. Any other state is final, so it is
 * trusted until the service purges the record. A read is idempotent and sends
 * no email, so it may retry.
 *
 * `staleTime` as a function: only `pending` changes
 * (https://tanstack.com/query/latest/docs/framework/react/reference/type-aliases/StaleTimeFunction).
 */
export function otpVerdictQueryOptions({ hash }: { hash: string }) {
  return queryOptions({
    queryKey: ['otp-verdict', hash],
    queryFn: () => getVerdict(hash),
    // Nothing observes the Verdict, so no background refetch ever runs. It is
    // only read with `query()`, which returns cached data while it is fresh and
    // fetches once it is stale. So `staleTime` decides when a read asks the
    // service again: always while `pending`, and for a final state only once
    // the service has purged the record (7 days, per its docs). That read gets
    // `NOT_FOUND`, which `isVerified` treats as not verified.
    staleTime: query =>
      query.state.data?.state === 'pending' ? 0 : OTP_RECORD_RETENTION_MS,
    gcTime: OTP_RECORD_RETENTION_MS,
    retry: 2,
  });
}

/**
 * The hash of the cached challenge of an email, if any. Reads the cache and
 * never fetches: fetching the challenge is a create, and a create sends an
 * email. The docs reserve `getQueryData` for functions like this one, not
 * components
 * (https://tanstack.com/query/latest/docs/framework/react/reference/classes/QueryClient#getquerydata).
 */
export function readChallengeHash({
  queryClient,
  email,
}: {
  queryClient: QueryClient;
  email: string;
}): string | undefined {
  return queryClient.getQueryData(otpChallengeQueryOptions({ email }).queryKey)
    ?.hash;
}

/**
 * Whether an email is a Verified email: its cached challenge has a `confirmed`
 * Verdict. The one definition, for the OTP route, the OTP pages and the gates.
 * It never creates a challenge, so asking never sends an email. No cached
 * challenge, a Verdict that is not `confirmed`, and a Verdict that cannot be
 * read all mean "not verified".
 *
 * The Verdict is read with `query()`: it returns fresh cached data without a
 * request, and throws on error
 * (https://tanstack.com/query/latest/docs/framework/react/reference/classes/QueryClient#query).
 */
export async function isVerified({
  queryClient,
  email,
}: {
  queryClient: QueryClient;
  email: string;
}): Promise<boolean> {
  const hash = readChallengeHash({ queryClient, email });
  if (!hash) {
    return false;
  }

  const verdict = await queryClient
    .query(otpVerdictQueryOptions({ hash }))
    .catch(() => undefined);

  return verdict?.state === 'confirmed';
}

export type Challenge =
  // Being resumed or created, on enter or after a restart. Show the loading state.
  | { kind: 'pending' }
  | { kind: 'ready'; hash: string }
  | { kind: 'rate-limited'; expiresAt: Date }
  // The create failed for another reason. The iframe renders without a hash on
  // purpose: its own error page offers "Try again" and posts `UNKNOWN_ERROR`
  // (ADR 022), which restarts the challenge.
  | { kind: 'unavailable' };

export function toChallenge({
  isPending,
  data,
  error,
}: {
  isPending: boolean;
  data: { hash: string } | undefined;
  error: Error | null;
}): Challenge {
  if (isPending) {
    return { kind: 'pending' };
  }

  if (data) {
    return { kind: 'ready', hash: data.hash };
  }

  if (error instanceof OtpRateLimitedError) {
    return { kind: 'rate-limited', expiresAt: error.expiresAt };
  }

  return { kind: 'unavailable' };
}
